import React, { useState } from 'react';

interface MetadataSectionProps {
  title: string;
  metadata: Record<string, any>;
  defaultExpanded?: boolean;
  className?: string;
}

const MetadataSection: React.FC<MetadataSectionProps> = ({
  title,
  metadata,
  defaultExpanded = false,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const renderValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          value ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {value ? 'true' : 'false'}
        </span>
      );
    }

    if (typeof value === 'number') {
      return <span className="text-blue-600 font-mono">{value}</span>;
    }

    if (typeof value === 'string') {
      // Check if it's a URL
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
          >
            {value}
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        );
      }
      return <span className="text-gray-700">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">[]</span>;
      }
      return (
        <div className="space-y-1" style={{ marginLeft: `${depth * 1}rem` }}>
          {value.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-gray-400">[{index}]</span>
              <div className="flex-1">{renderValue(item, depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span className="text-gray-400 italic">{'{}'}</span>;
      }
      return (
        <div className="space-y-2 border-l-2 border-gray-200 pl-3" style={{ marginLeft: `${depth * 1}rem` }}>
          {keys.map((key) => (
            <div key={key} className="space-y-1">
              <span className="text-sm font-medium text-gray-600">{key}:</span>
              <div className="ml-2">{renderValue(value[key], depth + 1)}</div>
            </div>
          ))}
        </div>
      );
    }

    return <span className="text-gray-600">{String(value)}</span>;
  };

  const keys = Object.keys(metadata);
  if (keys.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 shadow-sm ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <svg
          className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
            isExpanded ? 'transform rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          {keys.map((key) => (
            <div key={key} className="space-y-1">
              <span className="text-sm font-medium text-gray-700">{key}:</span>
              <div className="ml-2">{renderValue(metadata[key])}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MetadataSection;
